import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Fire, Warning, ShieldSlash } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * The last resort: emptying the running Society.
 *
 * Easy to FIND on purpose. In the situation this exists for, hunting through
 * menus is the wrong kind of friction — the guarding belongs on the action,
 * not on the door. Three things stand in front of it (superadmin, a freshly
 * re-entered password, a phrase typed out), and then a minute in which to stop.
 *
 * Cancelling is one tap with no ceremony at all. Stopping a destruction must
 * never be harder than starting one.
 */
const LastResort = () => {
  const [status, setStatus] = useState(null);
  const [unreachable, setUnreachable] = useState(false);
  const [password, setPassword] = useState('');
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const tick = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/burn`, { withCredentials: true });
      setStatus(data);
      setUnreachable(false);
    } catch (e) {
      // Refused is not the same as broken, and only one of them is worth
      // saying. A caller without the role should see nothing -- that is the
      // point of the role. But this tab only exists for somebody who HAS it,
      // so any other failure means the screen they came for is simply absent,
      // and rendering nothing tells them it was never built.
      const code = e.response?.status;
      setUnreachable(code !== 401 && code !== 403);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Counts down from whatever the server said, so a reloaded page still shows
  // the real deadline rather than restarting the minute.
  useEffect(() => {
    clearInterval(tick.current);
    if (!status?.armed || !status?.fires_at) { setRemaining(null); return undefined; }
    const target = new Date(status.fires_at).getTime();
    const update = () => setRemaining(Math.max(0, Math.round((target - Date.now()) / 1000)));
    update();
    tick.current = setInterval(update, 500);
    return () => clearInterval(tick.current);
  }, [status]);

  const arm = async () => {
    setBusy(true);
    try {
      // The step-up is a separate call so a wrong password never reaches the
      // arming endpoint at all.
      await axios.post(`${API_URL}/api/auth/reauth`, { password }, { withCredentials: true });
      await axios.post(
        `${API_URL}/api/admin/burn/arm`, { phrase }, { withCredentials: true },
      );
      setPassword(''); setPhrase('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not arm.');
    } finally {
      setBusy(false);
    }
  };

  const disarm = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/burn/disarm`, {}, { withCredentials: true });
      toast.success('Cancelled.');
      await load();
    } catch (e) {
      toast.error('Could not cancel — try again.');
    }
  };

  if (!status) {
    if (!unreachable) return null;
    return (
      <div className="border border-destructive/40 rounded-xl bg-card p-5 space-y-3" data-testid="burn-unreachable">
        <div className="flex items-center gap-2">
          <ShieldSlash className="w-5 h-5 text-destructive" weight="fill" />
          <h2 className="font-heading text-lg">Last resort</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          This screen could not be loaded. It is not missing and nothing is
          wrong with the Society — the server did not answer for it. Try again;
          if it keeps failing, the backend is the place to look.
        </p>
        <Button type="button" variant="outline" onClick={load} className="w-full">
          Try again
        </Button>
      </div>
    );
  }

  const inv = status.inventory || {};
  const ready = password.length > 0 && phrase === status.confirm_phrase;

  if (status.armed) {
    return (
      <div
        className="border-2 border-destructive rounded-xl bg-destructive/10 p-6 space-y-4 text-center"
        data-testid="burn-armed"
      >
        <Fire className="w-10 h-10 mx-auto text-destructive" weight="fill" />
        <div>
          <h2 className="font-heading text-2xl text-destructive">Burning in {remaining ?? '…'}s</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Everything below goes. The nightly backups do not — they are your way back.
          </p>
        </div>
        <Button
          type="button"
          onClick={disarm}
          className="w-full h-14 text-base bg-primary hover:bg-primary/90"
          data-testid="burn-disarm"
        >
          Stop
        </Button>
      </div>
    );
  }

  return (
    <div className="border border-destructive/40 rounded-xl bg-card p-5 space-y-4" data-testid="last-resort">
      <div className="flex items-center gap-2">
        <ShieldSlash className="w-5 h-5 text-destructive" weight="fill" />
        <h2 className="font-heading text-lg">Last resort</h2>
      </div>

      <p className="text-sm text-muted-foreground">
        Empties the running Society. Use it when leaving the data in place is
        worse than losing it.
      </p>

      {/* A confirmation that only repeats a word is not consent. */}
      <div className="grid grid-cols-2 gap-2 text-sm border border-border rounded-lg p-3 bg-muted/30">
        {[
          ['Members', inv.members], ['Threads', inv.threads],
          ['Messages', inv.messages], ['Attachments', inv.attachments],
          ['Vaults', inv.vaults], ['Notes', inv.notes],
          ['Capsules', inv.capsules], ['Audit entries', inv.audit_entries],
        ].map(([label, n]) => (
          <div key={label} className="flex justify-between gap-2">
            <span className="text-muted-foreground">{label}</span>
            <span className="tabular-nums font-medium text-foreground">{n ?? '—'}</span>
          </div>
        ))}
      </div>

      <div className="space-y-2 text-xs text-muted-foreground leading-relaxed">
        <p>
          <strong className="text-foreground">The nightly backups survive.</strong> They
          live on the machine itself, outside anything this app can reach — which
          is also why nobody who takes the app can destroy them. Restoring from
          one is how you come back.
        </p>
        <p>
          <strong className="text-foreground">Removing is not overwriting.</strong> Nothing
          can be read back through Arcanum afterwards, but the bytes stay on the
          drive until ordinary use writes over them, and on an SSD even
          deliberate overwriting is not a guarantee. This is "gone from the
          service", not "annihilated".
        </p>
        <p>
          <strong className="text-foreground">Members keep what they kept.</strong> Screenshots,
          things written down, anything copied out.
        </p>
      </div>

      <div className="space-y-2 pt-1">
        <Input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Your password"
          autoComplete="current-password"
          className="h-11"
          data-testid="burn-password"
        />
        <Input
          value={phrase}
          onChange={(e) => setPhrase(e.target.value)}
          placeholder={status.confirm_phrase}
          className="h-11 font-mono"
          data-testid="burn-phrase"
        />
        <p className="text-xs text-muted-foreground">
          Type <strong className="text-foreground font-mono">{status.confirm_phrase}</strong> exactly.
        </p>
      </div>

      <Button
        type="button"
        disabled={!ready || busy}
        onClick={arm}
        variant="destructive"
        className="w-full h-11"
        data-testid="burn-arm"
      >
        <Warning className="w-4 h-4 mr-2" weight="fill" />
        {busy ? 'Arming…' : `Arm — ${status.fuse_seconds} seconds to change your mind`}
      </Button>
    </div>
  );
};

export default LastResort;
